import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { offerUnits } from '@ooh/contracts';
import type {
  AddOfferUnitsRequest,
  AddOfferUnitsResponse,
  RemoveOfferUnitsRequest,
  UpdateOfferUnitRequest,
} from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { postAddOfferUnits } from './add-offer-units';
import { OfferConflictError, rethrowOfferConflict } from './errors';
import { offerAvailabilityKeys, offerKeys, offerUnitQueries, useEffectiveAuth } from './queries';
import type { WithCallWithAuth } from './queries';

interface UpdateOfferUnitVariables extends UpdateOfferUnitRequest {
  unitId: number;
}

type RemoveOfferUnitsVariables = { unitId: number } | RemoveOfferUnitsRequest;

export async function invalidateOfferQueries(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  offerId: number,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: offerUnitQueries.all(offerId) }),
    queryClient.invalidateQueries({ queryKey: offerKeys.detail(String(offerId)) }),
    queryClient.invalidateQueries({ queryKey: offerAvailabilityKeys.availability(offerId) }),
  ]);
}

export function useAddOfferUnits(offerId: number, auth?: WithCallWithAuth) {
  const effectiveAuth = useEffectiveAuth(auth);
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation<AddOfferUnitsResponse, Error, AddOfferUnitsRequest>({
    mutationFn: (body) => postAddOfferUnits(effectiveAuth, offerId, body.mediaUnitIds),
    onSuccess: async (data) => {
      await invalidateOfferQueries(queryClient, offerId);
      // Warn-only: added units may have reservations overlapping the offer's
      // default campaign date range — the add itself always succeeds
      // (add-units-conflict-warning).
      const conflictCount = data.conflictedMediaUnitIds?.length ?? 0;
      if (conflictCount > 0) {
        toast.warning(t('offerUnits.addConflictWarning', { count: conflictCount }));
      }
    },
  });
}

export function useUpdateOfferUnit(offerId: number, auth?: WithCallWithAuth) {
  const effectiveAuth = useEffectiveAuth(auth);
  const queryClient = useQueryClient();

  return useMutation<unknown, Error | OfferConflictError, UpdateOfferUnitVariables>({
    mutationFn: async ({ unitId, ...body }) => {
      try {
        return await effectiveAuth.callWithAuth(offerUnits.update, { offerId, unitId, ...body });
      } catch (error) {
        rethrowOfferConflict(error);
      }
    },
    onSuccess: async () => invalidateOfferQueries(queryClient, offerId),
  });
}

export function useRemoveOfferUnits(offerId: number, auth?: WithCallWithAuth) {
  const effectiveAuth = useEffectiveAuth(auth);
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, RemoveOfferUnitsVariables>({
    mutationFn: (body) =>
      'unitId' in body
        ? effectiveAuth.callWithAuth(offerUnits.remove, { offerId, unitId: body.unitId })
        : effectiveAuth.callWithAuth(offerUnits.removeBatch, { offerId, ...body }),
    onSuccess: async () => invalidateOfferQueries(queryClient, offerId),
  });
}
